import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';
import { Login } from '@/components/pages/Login';
import { UsersPage } from '@/components/pages/UsersPage';
import { ProductsPage } from '@/components/pages/ProductsPage';
import { SupportPage } from '@/components/pages/SupportPage';
import RealEstatePage from '@/components/pages/RealEstatePage';
import { CorretoresPage } from '@/components/pages/CorretoresPage';
import { DashboardPage } from '@/components/pages/DashboardPage';
import { LinktreeLanding } from '@/components/pages/LinktreeLanding';
import { PublicRegistrationWizard } from '@/components/pages/PublicRegistrationWizard';
import { ConfiguracoesPage } from '@/components/pages/ConfiguracoesPage';
import { AdminTenantsPage } from '@/components/pages/AdminTenantsPage';
import { LeadsPage } from '@/components/pages/LeadsPage';
import { User, PageId, Role, WhatsAppStatus } from '@/types';
import { fetchWhatsAppStatus as fetchWhatsAppStatusWithFallback } from '@/utils/whatsapp';
import { resolveApiBase } from '@/utils/apiBase';
import { ServicesProvider } from '@/contexts/ServicesContext';

const CORE_API = resolveApiBase(import.meta.env.VITE_CORE_API, '/core', 'http://localhost:3001');
const MANAGER_API = resolveApiBase(import.meta.env.VITE_MANAGER_API, '/manager-api', 'http://localhost:8789');
const WHATSAPP_API = resolveApiBase(import.meta.env.VITE_WHATSAPP_API, '/whatsapp', 'http://localhost:3030');
const BROWSER_API = resolveApiBase(import.meta.env.VITE_BROWSER_API, '/browser', 'http://localhost:3010');
const APP_MODE = (import.meta.env.VITE_APP_MODE || 'tenant').toLowerCase();
const AUTH_API = APP_MODE === 'console' ? MANAGER_API : CORE_API;
const IS_CONSOLE_MODE = APP_MODE === 'console';
const ROUTER_BASE = (() => {
  const fallbackBase = APP_MODE === 'console' && import.meta.env.PROD ? '/manager' : '/';
  const rawBase = import.meta.env.VITE_BASE || fallbackBase;
  if (rawBase === '/') return '/';
  const normalized = rawBase.replace(/\/+$/, '') || '/';
  // Evita tela em branco quando a app abre em "/" mas o basename ficou "/manager".
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname;
    const matchesBase = pathname === normalized || pathname.startsWith(`${normalized}/`);
    if (!matchesBase) return '/';
  }

  return normalized;
})();
const THEME_STORAGE_PREFIX = 'va_theme_';

const normalizeBase = (value: string) => {
  if (value.startsWith('http')) return value;
  if (value.startsWith('/')) return value;
  if (import.meta.env.PROD) return value;
  return `http://localhost:${value}`;
};

const buildServiceBase = (coreBase: string, port?: number | null): string | null => {
  if (!port) return null;
  if (import.meta.env.PROD) return null;
  try {
    const base = new URL(normalizeBase(coreBase));
    base.port = String(port);
    return base.origin;
  } catch {
    return `http://localhost:${port}`;
  }
};

type TicketStatus = 'pending' | 'open' | 'closed';

const pageToPath: Record<PageId, string> = {
  'dashboard': '/dashboard',
  'admin-tenants': '/admin-tenants',
  'usuarios': '/usuarios',
  'produtos': '/produtos',
  'imobiliarias': '/imobiliarias',
  'corretores': '/corretores',
  'atendimento': '/atendimento',
  'pesquisas': '/pesquisas',
  'configuracoes': '/configuracoes',
  'leads': '/leads',
  'public-wizard': '/:tenant/cadastro/:productId'
};

const resolvePageFromPath = (pathname: string): PageId => {
  if (pathname.match(/^\/[^/]+\/cadastro/)) return 'public-wizard';
  if (pathname.startsWith('/atendimento')) return 'atendimento';
  if (pathname.startsWith('/usuarios')) return 'usuarios';
  if (pathname.startsWith('/produtos')) return 'produtos';
  if (pathname.startsWith('/imobiliarias')) return 'imobiliarias';
  if (pathname.startsWith('/corretores')) return 'corretores';
  if (pathname.startsWith('/configuracoes')) return 'configuracoes';
  if (pathname.startsWith('/admin-tenants')) return 'admin-tenants';
  if (pathname.startsWith('/pesquisas')) return 'pesquisas';
  if (pathname.startsWith('/leads')) return 'leads';
  return 'dashboard';
};

const normalizeLoginWhatsapp = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  let local = digits.replace(/^0+/, '');
  if (local.startsWith('55')) local = local.slice(2);
  local = local.replace(/^0+/, '');
  if (local.length > 11) local = local.slice(0, 11);
  const trimmedLocal = local.slice(-11);
  return `55${trimmedLocal}`;
};

const loadThemePreference = (userId?: number | string | null): boolean | null => {
  if (!userId || typeof window === 'undefined') return null;
  const raw = localStorage.getItem(`${THEME_STORAGE_PREFIX}${userId}`);
  if (!raw) return null;
  return raw === 'dark';
};

const persistThemePreference = (userId: number | string | null | undefined, isDark: boolean) => {
  if (!userId || typeof window === 'undefined') return;
  localStorage.setItem(`${THEME_STORAGE_PREFIX}${userId}`, isDark ? 'dark' : 'light');
};

const tipoToRole = (tipo?: string): Role => {
  switch (tipo) {
    case 'ADMIN': return 'admin';
    case 'STAFF': return 'staff';
    case 'CLIENTE': return 'client';
    default: return 'staff';
  }
};

const mapApiStatus = (status?: string): TicketStatus => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'encerrado') return 'closed';
  if (normalized === 'em_atendimento' || normalized === 'aguardando_resposta_corretor') return 'open';
  return 'pending';
};

const persistTenantId = (candidate?: unknown, onLabel?: (label: string) => void) => {
  const fromResp =
    (candidate as any)?.tenantId ??
    (candidate as any)?.tenant_id ??
    (candidate as any)?.clienteId ??
    (candidate as any)?.cliente_id;
  const fromName =
    (candidate as any)?.tenantName ??
    (candidate as any)?.tenant_name;
  const fromEnv = import.meta.env.VITE_TENANT_ID || import.meta.env.VITE_TENANT_NAME;
  const chosen = fromResp || fromName || fromEnv || TENANT_NAME;
  if (chosen) {
    localStorage.setItem('tenantId', String(chosen));
    localStorage.setItem('va_tenant_id', String(chosen));
    localStorage.setItem('va_tenant_name', String(chosen));
    onLabel?.(String(chosen));
  }
};

const resolveTenantName = (): string => {
  const envName = import.meta.env.VITE_TENANT_NAME;
  const envId = import.meta.env.VITE_TENANT_ID;
  if (envName) return envName;
  if (envId) return envId;
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    const qsTenant = url.searchParams.get('tenant') || url.searchParams.get('tenantId') || url.searchParams.get('t');
    const lsTenant = localStorage.getItem('va_tenant_name') || localStorage.getItem('va_tenant_id');
    if (qsTenant && qsTenant.trim()) {
      const normalized = qsTenant.trim();
      localStorage.setItem('va_tenant_name', normalized);
      return normalized;
    }
    if (lsTenant && lsTenant.trim()) return lsTenant.trim();
    const host = window.location.hostname;
    const maybeSub = host.split('.')[0];
    if (maybeSub && maybeSub.toLowerCase() !== 'localhost') return maybeSub;
  }
  return 'Tenant';
};

const TENANT_NAME = resolveTenantName();

const RequireAuth: React.FC<{ isAuthenticated: boolean; redirectTo: string }> = ({ isAuthenticated, redirectTo }) => {
  if (!isAuthenticated) return <Navigate to={redirectTo} replace />;
  return <Outlet />;
};

const RequireRole: React.FC<{ role: Role; allowed: Role[]; fallback?: string }> = ({ role, allowed, fallback = '/dashboard' }) => {
  if (!allowed.includes(role)) return <Navigate to={fallback} replace />;
  return <Outlet />;
};

const RealEstateRoute: React.FC<{ darkMode: boolean; role: Role }> = ({ darkMode, role }) => {
  const { agencyId, tab } = useParams();
  return (
    <RealEstatePage
      darkMode={darkMode}
      role={role}
      initialAgencyId={agencyId ? Number(agencyId) : null}
      initialTabSlug={tab || null}
    />
  );
};

const CorretoresRoute: React.FC<{ darkMode: boolean }> = ({ darkMode }) => {
  const { brokerId } = useParams();
  return <CorretoresPage darkMode={darkMode} initialBrokerId={brokerId ? Number(brokerId) : null} />;
};

const PublicWizardRoute: React.FC<{ darkMode: boolean; onBack: () => void; success?: boolean }> = ({ darkMode, onBack, success = false }) => {
  const { tenant, productId } = useParams();
  if (tenant) {
    localStorage.setItem('va_tenant_name', tenant);
    localStorage.setItem('va_tenant_id', tenant);
  }
  return <PublicRegistrationWizard darkMode={darkMode} onBack={onBack} productId={productId ? Number(productId) : null} successOverride={success} />;
};

const ProductsRoute: React.FC<{ darkMode: boolean; role: Role; onNavigateToAgency?: (agencyId: number) => void }> = ({ darkMode, role, onNavigateToAgency }) => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const numericId = productId ? Number(productId) : null;
  // const tenantSlug = resolveTenantName();
  return (
    <ProductsPage
      darkMode={darkMode}
      role={role}
      // onNavigate={(page: PageId) => navigate(pageToPath[page] || '/dashboard')}
      onNavigateToAgency={onNavigateToAgency}
      initialProductId={numericId}
      onNavigateToProduct={(id: number) => navigate(`/produtos/${id}`)}
      onExitProduct={() => navigate('/produtos')}
      // onOpenPublicWizard={(id: number) => navigate(tenantSlug ? `/${tenantSlug}/cadastro/${id}` : `/cadastro/${id}`)}
    />
  );
};

const PrivateLayout: React.FC<{
  role: Role;
  darkMode: boolean;
  tenantLabel: string;
  tenantLogoUrl: string;
  user: User;
  pendingCount: number;
  newMessageTick: number;
  whatsappStatus: WhatsAppStatus | null;
  onLogout: () => void;
  toggleTheme: () => void;
  onNavigatePage: (page: PageId) => void;
  currentPage: PageId;
}> = ({ role, darkMode, tenantLabel, tenantLogoUrl, user, pendingCount, newMessageTick, whatsappStatus, onLogout, toggleTheme, onNavigatePage, currentPage }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  return (
    <div className={`flex h-screen w-full overflow-hidden transition-colors duration-300 ${darkMode ? 'bg-black' : 'bg-gray-50'}`}>
      <Sidebar
        role={role}
        currentPage={currentPage}
        onNavigate={onNavigatePage}
        darkMode={darkMode}
        tenantName={tenantLabel}
        logoUrl={tenantLogoUrl}
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
      <div className="flex-1 flex flex-col min-w-0">
        <Header
          user={user}
          darkMode={darkMode}
          toggleTheme={toggleTheme}
          onLogout={onLogout}
          pendingCount={pendingCount}
          newMessageTick={newMessageTick}
          whatsappStatus={whatsappStatus}
          onNavigate={(page) => onNavigatePage(page as PageId)}
          onToggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        />
        <main className={`flex-1 overflow-y-auto ${darkMode ? 'bg-black' : 'bg-white'}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const AppContent: React.FC = () => {
  if (APP_MODE === 'linktree') {
    return <LinktreeLanding />;
  }
  const location = useLocation();
  const navigate = useNavigate();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState<string>(() => localStorage.getItem('va_token') || '');
  const [role, setRole] = useState<Role>('admin');
  const [darkMode, setDarkMode] = useState(false);
  const [tenantLabel, setTenantLabel] = useState<string>(() => TENANT_NAME);
  const [pendingCount, setPendingCount] = useState(0);
  const [newMessageTick, setNewMessageTick] = useState(0);
  const [, setTicketStatusMap] = useState<Map<number, TicketStatus>>(() => new Map());
  const [user, setUser] = useState<User | null>(null);
  const [validatingToken, setValidatingToken] = useState(true);
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatus | null>(null);
  const [whatsappApiBase, setWhatsappApiBase] = useState<string>(WHATSAPP_API);
  const [browserApiBase, setBrowserApiBase] = useState<string>(BROWSER_API);

  const authHeaders = useMemo((): Record<string, string> => (authToken ? { Authorization: `Bearer ${authToken}` } : {}), [authToken]);
  const tenantLogoUrl = useMemo(() => {
    const base = CORE_API.replace(/\/$/, '');
    if (!authToken) return `${base}/branding/logo`;
    return `${base}/branding/logo?token=${encodeURIComponent(authToken)}`;
  }, [authToken]);
  const currentPage = resolvePageFromPath(location.pathname);
  const isPublicWizard = currentPage === 'public-wizard';

  const recalcPending = useCallback((map: Map<number, TicketStatus>) => {
    let pending = 0;
    map.forEach(status => {
      if (status === 'pending') pending += 1;
    });
    setPendingCount(pending);
  }, []);

  const updateStatusMap = useCallback((updater: (prev: Map<number, TicketStatus>) => Map<number, TicketStatus>) => {
    setTicketStatusMap(prev => {
      const next = updater(prev);
      recalcPending(next);
      return next;
    });
  }, [recalcPending]);

  useEffect(() => {
    if (isPublicWizard) {
      setValidatingToken(false);
      setIsAuthenticated(false);
      return;
    }
    const savedToken = localStorage.getItem('va_token');
    if (!savedToken) {
      setValidatingToken(false);
      setAuthToken('');
      return;
    }
    setAuthToken(savedToken);

    const validateToken = async () => {
      try {
        const res = await fetch(`${AUTH_API}/auth/me`, { headers: { Authorization: `Bearer ${savedToken}` } });
        if (res.ok) {
          const data = await res.json();
          const mappedRole = tipoToRole(data.tipo);
          persistTenantId(data, setTenantLabel);
          setUser({ id: data.id, name: data.nome, email: data.email, role: mappedRole });
          setRole(mappedRole);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('va_token');
          setAuthToken('');
        }
      } catch {
        localStorage.removeItem('va_token');
        setAuthToken('');
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [isPublicWizard]);

  useEffect(() => {
    if (!user?.id) return;
    const savedTheme = loadThemePreference(user.id);
    if (savedTheme !== null) {
      setDarkMode(savedTheme);
    }
  }, [user?.id]);

  useEffect(() => {
    if (APP_MODE !== 'console') return;
    document.body.classList.add('bg-black', 'text-white');
    document.body.classList.remove('bg-gray-50');
  }, []);

  useEffect(() => {
    if (APP_MODE === 'console') return;
    if (darkMode) {
      document.body.classList.add('bg-black', 'text-white');
      document.body.classList.remove('bg-gray-50');
    } else {
      document.body.classList.remove('bg-black', 'text-white');
      document.body.classList.add('bg-gray-50');
    }
  }, [darkMode]);

  useEffect(() => {
    if (APP_MODE === 'tenant') {
      document.title = `Visao | ${tenantLabel}`;
    } else {
      document.title = 'Visao | Console';
    }
  }, [tenantLabel]);

  const fetchWhatsAppStatus = useCallback(async () => {
    if (IS_CONSOLE_MODE) {
      setWhatsappStatus(null);
      return;
    }
    try {
      const base = whatsappApiBase || WHATSAPP_API;
      const status = await fetchWhatsAppStatusWithFallback(base, authHeaders);
      if (status) {
        setWhatsappStatus(status);
        return;
      }
    } catch (err) {
      console.error('Erro ao buscar status WhatsApp:', err);
    }
    setWhatsappStatus(null);
  }, [authHeaders, whatsappApiBase]);

  const fetchServicePorts = useCallback(async () => {
    if (IS_CONSOLE_MODE) return;
    try {
      const response = await fetch(`${CORE_API}/services/ports`, { headers: { ...authHeaders } });
      if (!response.ok) return;
      const data = await response.json().catch(() => ({}));
      const waBase = buildServiceBase(CORE_API, data?.ports?.whatsapp);
      const hasExplicitWhatsAppBase = (import.meta.env.VITE_WHATSAPP_API || '').includes('/whatsapp');
      if (waBase && (!import.meta.env.PROD || !hasExplicitWhatsAppBase)) setWhatsappApiBase(waBase);
      const browserBase = buildServiceBase(CORE_API, data?.ports?.browser);
      const hasExplicitBrowserBase = (import.meta.env.VITE_BROWSER_API || '').includes('/browser');
      if (browserBase && (!import.meta.env.PROD || !hasExplicitBrowserBase)) setBrowserApiBase(browserBase);
    } catch (err) {
      console.error('Erro ao buscar portas de servicos:', err);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (IS_CONSOLE_MODE || isPublicWizard || !isAuthenticated || !authToken) {
      setWhatsappStatus(null);
      return;
    }
    fetchWhatsAppStatus();
    const interval = setInterval(fetchWhatsAppStatus, 30000);
    return () => clearInterval(interval);
  }, [isPublicWizard, isAuthenticated, authToken, fetchWhatsAppStatus]);

  useEffect(() => {
    if (IS_CONSOLE_MODE || isPublicWizard || !isAuthenticated || !authToken) return;
    fetchServicePorts();
    const interval = setInterval(fetchServicePorts, 60000);
    return () => clearInterval(interval);
  }, [isPublicWizard, isAuthenticated, authToken, fetchServicePorts]);

  useEffect(() => {
    if (IS_CONSOLE_MODE || isPublicWizard || !isAuthenticated || !authToken) return;
    let cancelled = false;
    let source: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const coreBase = (CORE_API || '').replace(/\/$/, '');
    const url = `${coreBase}/atendimentos/events?token=${encodeURIComponent(authToken)}`;

    const refreshPendingSummary = async () => {
      try {
        const [filaRes, meusRes] = await Promise.all([
          fetch(`${CORE_API}/atendimentos/fila`, { headers: { ...authHeaders } }),
          fetch(`${CORE_API}/atendimentos/me`, { headers: { ...authHeaders } })
        ]);
        if (cancelled) return;
        const filaJson = filaRes.ok ? await filaRes.json() : { atendimentos: [] };
        const meusJson = meusRes.ok ? await meusRes.json() : { atendimentos: [] };
        updateStatusMap(() => {
          const next = new Map<number, TicketStatus>();
          (filaJson.atendimentos || []).forEach((a: any) => next.set(a.id, 'pending'));
          (meusJson.atendimentos || []).forEach((a: any) => next.set(a.id, mapApiStatus(a.status)));
          return next;
        });
      } catch (err) {
        console.error('Falha ao atualizar fila de atendimentos', err);
      }
    };

    const pingCore = async () => {
      if (!coreBase) return false;
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        const resp = await fetch(`${coreBase}/health`, { signal: controller.signal });
        clearTimeout(timeoutId);
        return resp.ok;
      } catch {
        return false;
      }
    };

    const startStream = async () => {
      const ok = await pingCore();
      if (cancelled) return;
      if (!ok) {
        retryTimer = setTimeout(startStream, 10000);
        return;
      }

      await refreshPendingSummary();

      if (source) source.close();
      source = new EventSource(url);
      source.addEventListener('atendimento.novo', ev => {
        try {
          const data = JSON.parse((ev as MessageEvent).data || '{}');
          if (!data.atendimentoId) return;
          updateStatusMap(prev => {
            const next = new Map(prev);
            next.set(data.atendimentoId, 'pending');
            return next;
          });
        } catch { /* ignore */ }
      });
      source.addEventListener('atendimento.assumido', ev => {
        try {
          const data = JSON.parse((ev as MessageEvent).data || '{}');
          if (!data.id) return;
          updateStatusMap(prev => {
            const next = new Map(prev);
            next.set(data.id, 'open');
            return next;
          });
        } catch { /* ignore */ }
      });
      source.addEventListener('atendimento.status', ev => {
        try {
          const data = JSON.parse((ev as MessageEvent).data || '{}');
          if (!data.id || !data.status) return;
          const status = mapApiStatus(data.status);
          updateStatusMap(prev => {
            const next = new Map(prev);
            next.set(data.id, status);
            return next;
          });
        } catch { /* ignore */ }
      });
      source.addEventListener('mensagem.recebida', () => {
        if (resolvePageFromPath(location.pathname) !== 'atendimento') {
          setNewMessageTick(prev => prev + 1);
        }
      });
    };

    startStream();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (source) source.close();
    };
  }, [isAuthenticated, authToken, authHeaders, updateStatusMap, location.pathname]);

  const handleLogin = async (whatsapp: string, password: string) => {
    const normalizedWhatsapp = normalizeLoginWhatsapp(whatsapp);
    const response = await fetch(`${AUTH_API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ whatsapp: normalizedWhatsapp, senha: password })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Credenciais invalidas');
    const mappedRole = tipoToRole(data.user?.tipo);
    persistTenantId(data, setTenantLabel);
    setUser({ id: data.user?.id, name: data.user?.nome, email: data.user?.email, role: mappedRole });
    setRole(mappedRole);
    setIsAuthenticated(true);
    localStorage.setItem('va_token', data.token || '');
    setAuthToken(data.token || '');
    navigate('/dashboard', { replace: true });
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('va_token');
    setAuthToken('');
    setTicketStatusMap(new Map());
    setPendingCount(0);
    setNewMessageTick(0);
    setDarkMode(false);
    setWhatsappStatus(null);
    navigate('/login', { replace: true });
  };

  const toggleTheme = () => setDarkMode((prev) => {
    const next = !prev;
    persistThemePreference(user?.id, next);
    return next;
  });
  const onNavigatePage = (page: PageId) => navigate(pageToPath[page] || '/dashboard');
  const handleNavigateToBroker = (brokerId: number) => navigate(`/corretores/${brokerId}`);
  const handleNavigateToAgency = (agencyId: number) => navigate(`/imobiliarias/${agencyId}/visao-geral`);

  if (APP_MODE === 'console') {
    if (!isAuthenticated || !user) return <Login onLogin={handleLogin} tenantName="Console" />;
    return <AdminTenantsPage />;
  }

  if (validatingToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-vision-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Verificando sessao...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LinktreeLanding />} />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login onLogin={handleLogin} tenantName={tenantLabel} />} />
      <Route path="/:tenant/cadastro/:productId" element={<PublicWizardRoute darkMode={darkMode} onBack={() => navigate('/produtos')} />} />
      <Route path="/:tenant/cadastro/:productId/sucesso" element={<PublicWizardRoute darkMode={darkMode} onBack={() => navigate('/produtos')} success />} />

      <Route element={<RequireAuth isAuthenticated={isAuthenticated} redirectTo="/login" />}>
        <Route element={<PrivateLayout
          role={role}
          darkMode={darkMode}
          tenantLabel={tenantLabel}
          tenantLogoUrl={tenantLogoUrl}
          user={user as User}
          pendingCount={pendingCount}
          newMessageTick={newMessageTick}
          whatsappStatus={whatsappStatus}
          onLogout={handleLogout}
          toggleTheme={toggleTheme}
          onNavigatePage={onNavigatePage}
          currentPage={currentPage}
        />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage darkMode={darkMode} role={role} />} />
          <Route path="/atendimento" element={<SupportPage
            darkMode={darkMode}
            onNavigateToBroker={handleNavigateToBroker}
            onPendingCountChange={setPendingCount}
            onNewMessage={() => setNewMessageTick(prev => prev + 1)}
            agentName={user?.name || 'Atendente'}
            role={role}
            userId={user?.id ? Number(user.id) : undefined}
            whatsappApiBase={whatsappApiBase}
          />} />
          <Route element={<RequireRole role={role} allowed={['admin']} />}>
            <Route path="/admin-tenants" element={<AdminTenantsPage />} />
            <Route path="/usuarios" element={<UsersPage darkMode={darkMode} />} />
            <Route path="/configuracoes" element={
              <ConfiguracoesPage
                darkMode={darkMode}
                tenantName={tenantLabel}
                role={role}
                whatsappApiBase={whatsappApiBase}
                browserApiBase={browserApiBase}
              />
            } />
          </Route>
          <Route element={<RequireRole role={role} allowed={['admin', 'staff']} />}>
            <Route path="/produtos/:productId?/*" element={<ProductsRoute darkMode={darkMode} role={role} onNavigateToAgency={handleNavigateToAgency} />} />
            <Route path="/imobiliarias" element={<RealEstatePage darkMode={darkMode} role={role} initialAgencyId={null} />} />
            <Route path="/imobiliarias/:agencyId/:tab?" element={<RealEstateRoute darkMode={darkMode} role={role} />} />
            <Route path="/corretores" element={<CorretoresPage darkMode={darkMode} initialBrokerId={null} />} />
            <Route path="/corretores/:brokerId" element={<CorretoresRoute darkMode={darkMode} />} />
            <Route path="/leads" element={<LeadsPage darkMode={darkMode} />} />
            <Route path="/pesquisas" element={<Navigate to="/dashboard" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />} />
    </Routes>
  );
};

const App: React.FC = () => (
  <ServicesProvider>
    {APP_MODE === 'linktree' ? (
      <LinktreeLanding />
    ) : (
      <BrowserRouter basename={ROUTER_BASE}>
        <AppContent />
      </BrowserRouter>
    )}
  </ServicesProvider>
);

export default App;



